import React, { useEffect, useState } from "react";
import CssBaseline from "@material-ui/core/CssBaseline";
import ListItem from "@material-ui/core/ListItem";
import List from "@material-ui/core/List";
import "./hidescrollbar.css";
import ListItemText from "@material-ui/core/ListItemText";
import NestedCategoryHorizontalScroll from "./nested-category-horizontal-scroll";
import { useWindowDimensions } from "./window-dimensions";
import { Typography } from "@material-ui/core";
import { evaluateResult } from "../../service/logic";
import { AnswerLinkPopup } from "./answer-link-popup";
import { Markup } from "interweave";
import { useEvaluationComponentStyles } from "../../styles/evaluation-component-style";

// defines background color for correct, missed and irrelevant
const getNavBGColor = (questionType) => {
  return [
    {
      name: "Correct",
      background: "#4CC87D",
      chosen: {
        background: "#4CC87D",
        borderRadius: "10px 10px 0px 0px",
        color: "white",
        zIndex: 10,
      },
      normal: {
        borderRadius: "10px 10px 0px 0px",
        color: "#EFEAEA",
        background: "#4CC87D",
        boxShadow: "20px 0 0 0 #4CC87D",
        zIndex: 7,
      },
    },
    {
      name: "Missed",
      background: "#7B0000",
      chosen: {
        background: "#7B0000",
        borderRadius: "10px 10px 0px 0px",
        color: "white",
        zIndex: 10,
      },
      normal: {
        borderRadius: "10px 10px 0px 0px",
        color: "#C9C9C9",
        background: "#7B0000",
        boxShadow:
          questionType === "Irrelevant"
            ? "20px 0 0 0 #7B0000"
            : "-20px 0 0 0 #7B0000",
        zIndex: 9,
      },
    },
    {
      name: "Irrelevant",
      background: "#3D6782",
      chosen: {
        background: "#3D6782",
        borderRadius: "10px 10px 0px 0px",
        color: "white",
        zIndex: 10,
      },
      normal: {
        borderRadius: "10px 10px 0px 0px",
        color: "#C9C9C9",
        background: "#3D6782",
        boxShadow: "-20px 0 0 0 #3D6782",
        zIndex: 7,
      },
    },
  ];
};

/**
 * EvaluationComponent is the component used to render the evaluations
 * @param parent options for the first layer of categories
 * @param parentID id to check if first layer is clicked
 * @param parentTitle label to render for the first layer
 * @param childID id to check if second layer is clicked
 * @param childTitle label to render for the second layer
 * @param getOpChildID option list id to fetch the children
 * @param getResultType category of results to fetch
 * @param childText text to display for the question
 * @returns {*}
 * @constructor
 */
const EvaluationComponent = ({
  parent,
  parentID,
  parentTitle,
  childID,
  childTitle,
  getOpChildID,
  getResultType,
  childText,
}) => {
  const classes = useEvaluationComponentStyles();

  const [selectedParent, setSelectedParent] = useState("");
  const [selectedChild, setSelectedChild] = useState("");
  const [question, setQuestion] = useState({ answer: { link: [] } });
  const [answerModal, setAnswerModal] = useState(false);
  const [child, setChild] = useState([]);
  const [questionType, setQuestionType] = useState("Correct");
  const [navBGColor, setNavBGColor] = useState([]);
  const [focusParent, setFocusParent] = useState("");
  const [focusChild, setFocusChild] = useState("");
  const [hideFocusParent, setHideFocusParent] = useState(false);
  const [hideFocusChild, setHideFocusChild] = useState(false);
  const [evaluationResult, setEvaluationResult] = useState({});
  const [missed, setMissed] = useState([]);
  const [selectedAndCorrect, setSelectedAndCorrect] = useState([]);
  const [selectedAndIncorrect, setSelectedAndIncorrect] = useState([]);

  const { width, height } = useWindowDimensions();
  const isWidthSm = width <= 600;

  useEffect(() => {
    setQuestion({ answer: { link: [] } });
    // if no result or parent / child is not selected, set the results to empty lists
    if (!selectedParent || !selectedChild || !evaluationResult) {
      setMissed([]);
      setSelectedAndCorrect([]);
      setSelectedAndIncorrect([]);
      return;
    }
    // filter and set the lists based on evaluation result and filters
    const {
      selectedAndCorrect,
      selectedAndIncorrect,
      missed,
    } = evaluationResult;
    if (!!missed)
      setMissed(
        missed.filter(
          (m) => m[parentID] === selectedParent && m[childID] === selectedChild
        )
      );
    if (!!selectedAndCorrect)
      setSelectedAndCorrect(
        selectedAndCorrect.filter(
          (m) => m[parentID] === selectedParent && m[childID] === selectedChild
        )
      );
    if (!!selectedAndIncorrect)
      setSelectedAndIncorrect(
        selectedAndIncorrect.filter(
          (m) => m[parentID] === selectedParent && m[childID] === selectedChild
        )
      );
  }, [childID, parentID, evaluationResult, selectedChild, selectedParent]);

  useEffect(() => {
    // identify css based on answer type
    setNavBGColor(getNavBGColor(questionType));
    // set results by type
    evaluateResult(getResultType).then((d) => {
      setEvaluationResult(d);
    });
  }, [getResultType, questionType]);

  const handleDesktopClick = (type) => {
    // close the answer sheet when user selects another question type
    setQuestionType(type);
    setNavBGColor(getNavBGColor(type));
    setAnswerModal(false);
    setQuestion({ answer: { link: [] } });
  };

  const toggleQuestion = (item) => {
    if (!!question && question.id === item.id) {
      // collapsed selected question
      setQuestion({ answer: { link: [] } });
      setAnswerModal(false);
    } else {
      setQuestion(item);
      setAnswerModal(true);
    }
  };

  // render result
  const renderResult = () => {
    const arr =
      questionType === "Correct"
        ? selectedAndCorrect //correct
        : questionType === "Irrelevant"
        ? selectedAndIncorrect //irrelevant
        : missed; //missed

    return (
      <>
        {isWidthSm ? ( //phone view
          <List
            id={"resultMobile"}
            className={classes.phoneresultlist}
            style={{ height: height / 2.5 }}
          >
            {/* no answer modal for irrelevant*/}
            {arr === selectedAndIncorrect
              ? arr.map((item) => (
                  <ListItem key={item.id} className={classes.phoneresultitem}>
                    <ListItemText primary={item[childText]} />
                  </ListItem>
                ))
              : arr.map((item) => (
                  <>
                    <ListItem
                      key={item.id}
                      className={classes.phoneresultitem}
                      style={
                        !!question
                          ? item.id === question.id
                            ? { color: "#F8F8F8", backgroundColor: "#457B9D" }
                            : null
                          : null
                      }
                      onClick={() => toggleQuestion(item)}
                    >
                      <ListItemText primary={item[childText]} />
                    </ListItem>

                    {/*show the answer*/}
                    {answerModal && question.id === item.id ? (
                      <ListItem
                        className={classes.phoneresultitem}
                        style={{
                          backgroundColor: "#FFFFFF",
                          cursor: "context-menu",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <Markup content={question.answer.contents} />
                        </div>
                        <ListItem>
                          <AnswerLinkPopup linkArray={question.answer.link} />
                        </ListItem>
                      </ListItem>
                    ) : null}
                  </>
                ))}
          </List>
        ) : (
          //desktop view
          <div
            className={classes.questionlist}
            style={{
              height: height - 450,
              minHeight: 150,
              maxHeight: height - 450,
            }}
          >
            {arr.map((item) => (
              <ListItem
                key={item.id}
                className={classes.question}
                style={
                  question
                    ? question.id === item.id
                      ? {
                          backgroundColor:
                            arr === selectedAndCorrect ? "#4CC87D" : "#F02C2C", //change color of selected button
                          color: "white",
                        }
                      : null
                    : null
                }
                onClick={() => {
                  if (arr !== selectedAndIncorrect) toggleQuestion(item);
                }}
              >
                <Typography variant="h4">{item[childText]}</Typography>
              </ListItem>
            ))}
          </div>
        )}
      </>
    );
  };

  const phoneEvaluation = () => {
    //display question type list for phone and then renderResult()
    return (
      <>
        <div style={{ width: "100%", justifyContent: "center" }}>
          <List id={"questionArea"} className={classes.phonenavlist}>
            {navBGColor.map((item) => (
              <ListItem
                button
                key={item.name}
                className={classes.phonenavtab}
                style={{
                  background:
                    questionType === item.name ? item.background : "#9F9F9F",
                  width: width / 4,
                }}
                onClick={() => {
                  setQuestionType(item.name);
                }}
              >
                <Typography variant="h4">{item.name}</Typography>
              </ListItem>
            ))}
          </List>
          {renderResult()}
        </div>
      </>
    );
  };

  const [isOverflow, setIsOverflow] = useState(false);
  useEffect(() => {
    setTimeout(() => {
      if (
        document.getElementById("answer") &&
        document.getElementById("answer").clientHeight > 175
      )
        setIsOverflow(true);
      else setIsOverflow(false);
    });
  });

  const evaluation = () => {
    return (
      <>
        <NestedCategoryHorizontalScroll
          parent={parent}
          parentID={parentID}
          parentTitle={parentTitle}
          childID={childID}
          childTitle={childTitle}
          getOpChildID={getOpChildID}
          selectedParent={selectedParent}
          setSelectedParent={setSelectedParent}
          selectedChild={selectedChild}
          setSelectedChild={setSelectedChild}
          child={child}
          setChild={setChild}
          focusParent={focusParent}
          setFocusParent={setFocusParent}
          focusChild={focusChild}
          setFocusChild={setFocusChild}
          hideFocusParent={hideFocusParent}
          setHideFocusParent={setHideFocusParent}
          hideFocusChild={hideFocusChild}
          setHideFocusChild={setHideFocusChild}
          width={width}
          isWidthSm={isWidthSm}
        />
        {selectedParent !== "" && selectedChild !== "" ? (
          <>
            {isWidthSm ? (
              phoneEvaluation()
            ) : (
              <div className={classes.bottom}>
                <div id={"questionFullArea"} className={classes.questionmodal}>
                  <div id={"questionArea"} className={classes.questiontypelist}>
                    {navBGColor.map((
                      item //display question type list here
                    ) => (
                      <div
                        key={item.name}
                        style={
                          questionType === item.name ? item.chosen : item.normal
                        }
                        className={classes.questiontype}
                        onClick={() => handleDesktopClick(item.name)}
                      >
                        <Typography variant="h4">{item.name}</Typography>
                      </div>
                    ))}
                  </div>
                  <div
                    className={classes.questionarea}
                    style={{
                      height: height - 432,
                      minHeight: 175,
                      maxHeight: height - 432,
                      borderRadius: " 0 0 10px 10px",
                    }}
                  >
                    {renderResult()}
                  </div>
                </div>

                {/*display answer here*/}
                <div className={classes.answermodal}>
                  {answerModal ? (
                    <div
                      className={classes.answerContainer}
                      style={{
                        justifyContent: isOverflow ? "flex-start" : "center",
                        maxHeight: height - 430,
                        minHeight: 175,
                      }}
                    >
                      <div className={classes.answerText} id={"answer"}>
                        <Markup content={question.answer.contents} />
                      </div>
                      <AnswerLinkPopup linkArray={question.answer.link} />
                    </div>
                  ) : (
                    <></>
                  )}
                </div>
              </div>
            )}
          </>
        ) : null}
      </>
    );
  };

  return (
    <React.Fragment>
      <CssBaseline />
      {evaluation()}
    </React.Fragment>
  );
};

export default EvaluationComponent;
